import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { PermissionV2Service } from '../permission-v2-service';
import type { FeatureInitResponse, FeatureKey, UserPlan } from '../../types';
import { FeatureInitApi } from '../../apis/feature-init-api';
import { PermissionV2Model } from '../../models/permission-v2-model';
import { QueryFeaturePermissionsApi } from '../../apis/query-feature-permissions-api';
import { ShareModel } from '../../models/share-model';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

@injectable()
export class PermissionV2ServiceImpl implements PermissionV2Service {
    private _onDataChangeEmitter = new Emitter<FeatureInitResponse>();
    readonly onDataChange = this._onDataChangeEmitter.event;

    constructor(
        @inject(FeatureInitApi)
        private _featureInitApi: FeatureInitApi,
        @inject(QueryFeaturePermissionsApi)
        private _queryFeaturePermissionsApi: QueryFeaturePermissionsApi,
        @inject(PermissionV2Model)
        private _model: PermissionV2Model,
        @inject(ShareModel)
        private readonly _shareModel: ShareModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        const data = this._model.modelData;
        if (data) {
            this._onDataChangeEmitter.fire(data);
        }
    }

    async initialize(options: { taskId: string, taskType: string }): Promise<void> {
        const isLogin = await isUserLogin();
        if (!isLogin) {
            return;
        }
        let isOwner = true;
        try {
            const data = await this._queryFeaturePermissionsApi.queryFeaturePermissions({
                task_id: options.taskId,
                task_type: this._convert(options.taskType),
            });
            isOwner = data.is_owner;
        } catch (err) {
            console.warn(err);
        }
        console.log('isOwner',isOwner);
        this._shareModel.update(state => {
            state.isShareModel = !isOwner;
            console.log('isShareModel',state.isShareModel);
            return state;
        });

        const data = await this._featureInitApi.getFeatureInit();
        this._model.update(() => data);
    }

    private _convert(taskType: string): 'AI_FTO_SEARCH' | 'AI_NOVELTY_SEARCH' | 'AI_SPECIFICATION' {
        if (taskType === 'novelty-check-report') {
            return 'AI_NOVELTY_SEARCH';
        }
        if (taskType === 'fto-pro') {
            return 'AI_FTO_SEARCH';
        }
        if (taskType === 'specification') {
            return 'AI_SPECIFICATION';
        }
        throw new Error(`预期外的任务类型：${taskType}`);
    }

    getFeatureData(): FeatureInitResponse | null {
        return this._model.modelData;
    }

    isAvailable(featureKey: FeatureKey): boolean {
        const data = this._model.modelData;
        if (!data) {
            return false;
        }
        return data.features[featureKey]?.isAvailable ?? false;
    }

    hasQuota(featureKey: FeatureKey): boolean {
        const data = this._model.modelData;
        if (!data) {
            return false;
        }
        const feature = data.features[featureKey];
        if (!feature) {
            return false;
        }
        return !feature.isQuotaExceeded;
    }

    getUserPlan(): UserPlan | null {
        return this._model.modelData?.userPlan ?? null;
    }

    updateFeatureData(data: FeatureInitResponse): void {
        this._model.update(() => data);
    }
}
